import { TOCNode } from '../types/DocumentTypes';
import { ProgressState, ProgressData } from './ProgressState';
import { ProgressObserver } from './ProgressObserver';
import { ProgressSync } from './ProgressSync';
import {
  blockIdInsideChapter,
  findChapterRecursively,
  findChapterByNodeId,
} from '../features/ContentRendering/utils/chapterUtils';
import { addEventToGTMDataLayer } from '../../src/utilities/gtm';

export interface ReadingLocation {
  chapter: TOCNode;
  hasProgress: boolean;
}

export interface ContinueReadingModalData {
  currentBlock: number;
  progressPercentage: number;
  targetChapter: TOCNode;
}

export interface ReadingSessionInitResult {
  action: 'navigate' | 'show_continue_modal';
  location: ReadingLocation;
  modalData?: ContinueReadingModalData;
}

export interface NavigationTarget {
  chapterId?: number | null;
}

export interface ModalState {
  show: boolean;
  data: ContinueReadingModalData | null;
}

/**
 * Enhanced Progress Manager - handles all reading session logic including modal
 *
 * IMPROVED VERSION: Uses configurable business rules and fixes race conditions
 * while maintaining backward compatibility with existing API
 */
export class ProgressManager {
  private readonly state: ProgressState;
  private readonly observer: ProgressObserver;
  private readonly sync: ProgressSync;
  private syncTimeout: ReturnType<typeof setTimeout> | null = null;
  private isTracking = false;
  public progressLoadPromise: Promise<void>; // Make public for external waiting
  // Timing constants
  private readonly trackingStartDelayMs = 100;
  private readonly syncDelayMs = 2000;

  // Modal state management
  private modalState: ModalState = { show: false, data: null };
  private readonly modalSubscribers = new Set<(state: ModalState) => void>();

  // Prevent tracking until user makes decision
  private userHasMadeDecision = false;
  // Store original saved progress separately to prevent overwriting
  private originalSavedProgress: {
    currentBlock: number;
  } | null = null;
  private lastSavedBlockId: number | null = null;

  // Configuration

  constructor(
    private readonly isbn: string,
    private readonly getAuthToken: () => Promise<string | undefined>,
    totalBlocks: number,
  ) {
    // Initialize configuration with validation

    this.state = new ProgressState(totalBlocks);
    this.sync = new ProgressSync(isbn, getAuthToken);
    this.observer = new ProgressObserver((blockId) =>
      this.handleBlockChange(blockId),
    );

    // Load initial progress from backend and store the promise
    this.progressLoadPromise = this.loadInitialProgress();
  }

  // ===== MODAL MANAGEMENT =====

  subscribeToModal(callback: (state: ModalState) => void): () => void {
    this.modalSubscribers.add(callback);
    return () => this.modalSubscribers.delete(callback);
  }

  private notifyModalSubscribers(): void {
    this.modalSubscribers.forEach((callback) => callback(this.modalState));
  }

  showModal(data: ContinueReadingModalData): void {
    this.modalState = { show: true, data };
    addEventToGTMDataLayer({
      event: 'VKH_show_continue_reading_from_last_saved_position_modal',
    });
    this.notifyModalSubscribers();
  }

  hideModal(): void {
    this.modalState = { show: false, data: null };
    this.notifyModalSubscribers();
  }

  // ===== HIGH-LEVEL READING SESSION API =====

  /**
   * Initialize a reading session with business logic
   * Handles URL parameters, saved progress, and modal decisions
   */
  async initializeReadingSession(
    chapters: TOCNode[],
    searchParams: URLSearchParams,
  ): Promise<ReadingSessionInitResult> {
    await this.progressLoadPromise;

    // Step 1: Determine navigation target from URL parameters
    const urlTarget = this.parseUrlParameters(searchParams);

    const navigationLocation = this.resolveNavigationLocation(
      chapters,
      urlTarget,
    );

    // Step 2: Check if we should show continue reading modal
    const modalDecision = await this.shouldShowContinueReadingModal(
      navigationLocation,
      chapters,
    );

    if (modalDecision.shouldShow && modalDecision.data) {
      this.showModal(modalDecision.data);

      return {
        action: 'show_continue_modal',
        location: navigationLocation,
        modalData: modalDecision.data,
      };
    } else {
      this.userHasMadeDecision = true;
    }

    return {
      action: 'navigate',
      location: navigationLocation,
    };
  }

  /**
   * Handle continue reading action from modal
   */
  async handleContinueReading(chapters: TOCNode[]): Promise<ReadingLocation> {
    await this.progressLoadPromise;

    if (!this.originalSavedProgress?.currentBlock) {
      throw new Error('No saved progress to continue from');
    }

    const savedBlockId = this.originalSavedProgress.currentBlock;

    const targetChapter = findChapterRecursively(
      chapters,
      blockIdInsideChapter(savedBlockId),
    );

    // Hide modal and mark decision as made
    this.hideModal();
    this.userHasMadeDecision = true;
    addEventToGTMDataLayer({
      event: 'VKH_continue_reading_from_last_saved_position',
    });

    // Update state to the original saved position
    this.state.updateProgress(savedBlockId);

    // The user is just choosing to navigate back to their existing saved position
    return {
      chapter: targetChapter || chapters[0],
      hasProgress: true,
    };
  }

  /**
   * Dismiss the continue reading modal
   */
  async dismissModal(): Promise<void> {
    // If nothing to close, resolve immediately
    if (!this.modalState.show) {
      return;
    }

    // User chose to stay at current position - mark decision as made
    this.userHasMadeDecision = true;
    addEventToGTMDataLayer({
      event: 'VKH_dismiss_continue_reading_from_last_saved_position',
    });

    // Clear any pending debounced syncs to prevent conflicts
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
      this.syncTimeout = null;
    }

    // Hide the modal and resolve immediately; UI close animation can continue
    this.hideModal();
  }

  /**
   * Parse URL parameters into navigation target
   */
  private parseUrlParameters(searchParams: URLSearchParams): NavigationTarget {
    const chapterIdParam = searchParams.get('chapterId');
    const chapterIdParsed =
      chapterIdParam === null ? null : parseInt(chapterIdParam, 10);

    return {
      chapterId:
        chapterIdParsed !== null && !Number.isNaN(chapterIdParsed)
          ? chapterIdParsed
          : null,
    };
  }

  /**
   * Resolve navigation target to actual location
   * Priority: chapterId > first chapter
   */
  private resolveNavigationLocation(
    chapters: TOCNode[],
    target: NavigationTarget,
  ): ReadingLocation {
    // Priority 1: ChapterId (nodeId; 0 is valid)
    if (target.chapterId !== null && target.chapterId !== undefined) {
      // First try nodeId match
      const byNode = findChapterByNodeId(chapters, target.chapterId);
      if (byNode) {
        return { chapter: byNode, hasProgress: false };
      }
    }

    // Priority 2: Default to first chapter
    return {
      chapter: chapters[0],
      hasProgress: false,
    };
  }

  /**
   * Determine if continue reading modal should be shown
   * Simplified logic: Show if user has saved progress in a different chapter
   */
  private async shouldShowContinueReadingModal(
    navigationLocation: ReadingLocation,
    chapters: TOCNode[],
  ): Promise<{ shouldShow: boolean; data?: ContinueReadingModalData }> {
    // Never show modal if already in active session
    if (this.state.isSessionActive()) {
      return { shouldShow: false };
    }

    const savedBlockId = this.originalSavedProgress?.currentBlock;

    // Must have saved progress
    if (!this.originalSavedProgress || !savedBlockId) {
      return { shouldShow: false };
    }

    // Find the chapter for saved progress
    const savedChapter = findChapterRecursively(
      chapters,
      blockIdInsideChapter(savedBlockId),
    );

    if (!savedChapter) {
      return { shouldShow: false };
    }

    // Show modal if saved progress is in a different chapter than navigation target
    const isDifferentChapter =
      savedChapter.nodeId !== navigationLocation.chapter.nodeId;

    if (!isDifferentChapter) {
      return { shouldShow: false };
    }

    // Distance threshold: only show if saved position is far from the target (>=200 blocks)
    const distanceThreshold = 200;
    const targetFirstBlock = navigationLocation.chapter.blockId;
    const isFarEnough =
      Math.abs(savedBlockId - targetFirstBlock) >= distanceThreshold;
    if (!isFarEnough) {
      return { shouldShow: false };
    }

    // Calculate percentage based on original saved progress
    const totalBlocks = this.state.getData().totalBlocks ?? 0;
    const progressPercentage =
      totalBlocks > 0 ? Math.round((savedBlockId / totalBlocks) * 100) : 0;

    return {
      shouldShow: true,
      data: {
        currentBlock: savedBlockId,
        targetChapter: savedChapter,
        progressPercentage,
      },
    };
  }

  // Scrolling handled by navigation layer

  // ===== EXISTING API (keeping for backward compatibility) =====

  subscribe(callback: (state: ProgressData) => void): () => void {
    return this.state.subscribe(callback);
  }

  // Expose tracking state for callers to avoid duplicate starts
  isTrackingActive(): boolean {
    return this.isTracking;
  }

  setCurrentPosition(blockId: number): void {
    // Only update if position actually changed
    const currentData = this.state.getData();

    if (currentData.currentBlock === blockId) {
      return;
    }

    // Update progress state immediately (no backend sync here; sync is scroll-driven)
    this.state.updateProgress(blockId);
  }

  /**
   *
   */

  destroy(): void {
    this.observer.stopObserving();
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
    }
    // Clear modal state
    this.hideModal();
    this.modalSubscribers.clear();
  }

  // Private methods
  private async loadInitialProgress(): Promise<void> {
    try {
      const backendData = await this.sync.loadProgress();

      // Store original progress separately to prevent overwriting
      this.originalSavedProgress = backendData;
      this.lastSavedBlockId = backendData?.currentBlock ?? null;

      this.state.loadFromBackend(backendData?.currentBlock ?? null);
    } catch (_error) {
      // Failed to load initial progress - will proceed without saved progress
      console.info('Failed to load initial progress', _error);
    }
  }

  private handleBlockChange(blockId: number): void {
    // Don't track changes until user has made a decision about modal
    if (!this.userHasMadeDecision) {
      return;
    }

    // Update state
    this.state.updateProgress(blockId);

    // Debounced sync to backend (scroll-driven)
    this.debouncedSync();
  }

  private debouncedSync(): void {
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
    }
    this.syncTimeout = setTimeout(() => {
      this.syncToBackend();
    }, this.syncDelayMs);
  }

  private async syncToBackend(): Promise<void> {
    // Never sync to backend until user has made a decision about modal
    if (!this.userHasMadeDecision) {
      return;
    }

    const currentData = this.state.getData();

    if (currentData.currentBlock) {
      // Skip redundant save if block hasn't changed compared to last saved
      if (this.lastSavedBlockId === currentData.currentBlock) {
        return;
      }
      try {
        await this.sync.saveProgress(currentData.currentBlock);
        this.lastSavedBlockId = currentData.currentBlock;
      } catch (_error) {
        // Failed to sync progress - will retry on next change
        console.info('Failed to sync progress', _error);
      }
    }
  }

  // Backend status is ignored for reading-progress flow

  // New unified method to start or refresh tracking as needed
  ensureTracking(): void {
    if (!this.isTracking) {
      this.isTracking = true;
      this.state.markSessionActive();
      setTimeout(() => {
        this.observer.startObserving();
      }, this.trackingStartDelayMs);
      return;
    }
    this.observer.refreshContent();
  }
}
